import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import styles from './styles.module.scss';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronIcon } from 'assets';
import { QUESTION_CARD } from 'components/reusable-questions/card-classes';
import {
  QuestionSingleRadio,
  QuestionMultiCheckbox,
  QuestionScale,
  QuestionRanking,
  QuestionCheckboxWithRating,
  QuestionSkillsRating,
  QuestionTextarea,
} from 'components/reusable-questions';
import VerticalRankingField from 'components/vertical-drag-input';
import { RankingField } from 'components/drag-drop-input';
import {
  OPEN_ENDED_RESPONSE_MIN_WORDS,
  meetsOpenEndedResponseMinimum,
} from 'utils/openEndedResponse';

// Candidate guide video ("How to Approach Your Precision Assessment").
// Set REACT_APP_CANDIDATE_GUIDE_VIDEO_URL to swap the video without a code
// change; the literal below is the fallback. Accepts any YouTube URL form.
// TODO: placeholder video (Blender's "Big Buck Bunny") so the block is visible
// while previewing — replace with the real guide link once it lands.
const CANDIDATE_GUIDE_VIDEO_URL = 'https://youtu.be/tsUfnYSL3Rk'

/**
 * Pulls the 11-character video id out of a watch/youtu.be/embed/shorts URL.
 * Returns null for anything unrecognised so the video block can be skipped
 * rather than rendering a broken player.
 */
const getYouTubeVideoId = (url: string): string | null => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?(?:.*&)?v=)([\w-]{11})/,
    /(?:youtu\.be\/)([\w-]{11})/,
    /(?:youtube\.com\/(?:embed|v|shorts|live)\/)([\w-]{11})/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  // Allow a bare video id to be configured directly.
  return /^[\w-]{11}$/.test(url.trim()) ? url.trim() : null;
};

// Loads the thumbnail only until the candidate hits play, so the guide screen
// doesn't pull in YouTube's player on every pre-assessment view.
const GuideVideo: React.FC<{ videoId: string }> = ({ videoId }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const frameClasses = 'relative block w-full aspect-video mb-5 overflow-hidden rounded-xl bg-[#0a1f5c]';

  if (isPlaying) {
    return (
      <div className={frameClasses}>
        <iframe
          className="block w-full h-full border-0"
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
          title="How to Approach Your Precision Assessment"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`${frameClasses} group border-0 p-0 cursor-pointer`}
      onClick={() => setIsPlaying(true)}
      aria-label="Play the candidate guide video"
    >
      <img
        className="block w-full h-full object-cover"
        src={`https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`}
        alt=""
        onError={(e) => {
          // Not every video has a maxres thumbnail; hqdefault always exists.
          const img = e.currentTarget;
          if (!img.src.includes('hqdefault')) {
            img.src = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
          }
        }}
      />
      <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-90 transition-opacity group-hover:opacity-100">
        <svg className="w-[52px] h-[37px] sm:w-[68px] sm:h-12" viewBox="0 0 68 48" aria-hidden="true">
          <path
            d="M66.52 7.74a8.55 8.55 0 0 0-6-6C55.79 0 34 0 34 0S12.21 0 7.48 1.74a8.55 8.55 0 0 0-6 6A89.51 89.51 0 0 0 0 24a89.51 89.51 0 0 0 1.48 16.26 8.55 8.55 0 0 0 6 6C12.21 48 34 48 34 48s21.79 0 26.52-1.74a8.55 8.55 0 0 0 6-6A89.51 89.51 0 0 0 68 24a89.51 89.51 0 0 0-1.48-16.26Z"
            fill="#f00"
          />
          <path d="M27.2 34.29 45.3 24 27.2 13.71Z" fill="#fff" />
        </svg>
      </span>
    </button>
  );
};

interface FormData {
  question1: string;
  question2: string[];
  question3: string;
  question4: string;
  question5: string;
  question5Rating: number | null;
  learningTools: number;
  organizingInformation: number;
  attentionToDetail: number;
  communicatingIdeas: number;
  solvingProblems: number;
  managingResponsibilities: number;
  workingWithPeople: number;
  question7: string;
}

export const SampleQuestionsComponent: React.FC = () => {
  // The sample questions sit behind an accordion so the guide opens on the
  // video, with the practice questions a click away.
  const [isSampleQuestionsOpen, setIsSampleQuestionsOpen] = useState(false);
  const guideVideoId = getYouTubeVideoId(CANDIDATE_GUIDE_VIDEO_URL);

  const schema = yup.object().shape({
    question1: yup.string().required('Please select an option').test(
      'max-one',
      'Only one option can be selected',
      value => value !== undefined && value.length <= 1
    ),
    question2: yup.array().of(yup.string()).required('Please select options').min(1, 'At least one option must be selected').max(3, 'Maximum 3 options can be selected'),
    question3: yup.string().required('Please select an option').test(
      'max-one',
      'Only one option can be selected',
      value => value !== undefined && value.length > 0
    ),
    question4: yup.string().required('Please select an option').test(
      'max-one',
      'Only one option can be selected',
      value => value !== undefined && value.length > 0
    ),
    question5: yup.string().required('Please select an option').test(
      'max-one',
      'Only one option can be selected',
      value => value !== undefined && value.length > 0
    ),
    question5Rating: yup.number()
      .nullable()
      .when('question5', (question5, schema) => {
        return question5 && question5.length > 0
          ? schema
            .min(1, 'Rating must be at least 1')
            .max(5, 'Rating must be at most 5')
            .required('Rating is required when an option is selected')
          : schema.nullable();
      }),
    learningTools: yup.number().min(0).max(7).required('Rating is required'),
    organizingInformation: yup.number().min(0).max(7).required('Rating is required'),
    attentionToDetail: yup.number().min(0).max(7).required('Rating is required'),
    communicatingIdeas: yup.number().min(0).max(7).required('Rating is required'),
    solvingProblems: yup.number().min(0).max(7).required('Rating is required'),
    managingResponsibilities: yup.number().min(0).max(7).required('Rating is required'),
    workingWithPeople: yup.number().min(0).max(7).required('Rating is required'),
    question7: yup.string().test(
      'min-words',
      `Response must be at least ${OPEN_ENDED_RESPONSE_MIN_WORDS} words, or left empty`,
      meetsOpenEndedResponseMinimum,
    ),
  });

  const { register, handleSubmit, watch, setValue, trigger, control, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      question1: '',
      question2: [],
      question3: '',
      question4: '',
      question5: '',
      learningTools: 0,
      organizingInformation: 0,
      attentionToDetail: 0,
      communicatingIdeas: 0,
      solvingProblems: 0,
      managingResponsibilities: 0,
      workingWithPeople: 0,
      question7: ''
    }
  });

  const [selectedSkills, setSelectedSkills] = useState({
    learningTools: 0,
    organizingInformation: 0,
    attentionToDetail: 0,
    communicatingIdeas: 0,
    solvingProblems: 0,
    managingResponsibilities: 0,
    workingWithPeople: 0,
  });

  const handleRatingChange = (skill: string, rating: number) => {
    setSelectedSkills(prev => ({
      ...prev,
      [skill]: rating,
    }));
    setValue(skill as keyof FormData, rating);
  };

  const question1Options = [
    { label: 'Wait until someone notices the issue before mentioning it.', value: 'wait' },
    { label: 'Complete only your own part of the work and assume others will handle the rest.', value: 'own_part' },
    { label: 'Share the missing information with the appropriate people, clarify expectations, and offer support where needed.', value: 'share' },
    { label: 'Complain privately to a coworker about the lack of communication.', value: 'complain' },
  ];

  const question2Options = [
    { label: 'Ask questions when something is unclear.', value: 'ask_questions' },
    { label: 'Explore the available guides or training materials.', value: 'explore_guides' },
    { label: 'Avoid using the new system until someone shows you every step.', value: 'avoid_until_shown' },
    { label: 'Practice using the system on real or sample tasks.', value: 'practice' },
    { label: 'Continue using the old process because it feels more familiar.', value: 'keep_old_process' },
    { label: 'Share helpful tips with teammates after learning something useful.', value: 'share_tips' },
  ];

  const question3Options = [
    { label: 'Always true', value: 'always_true' },
    { label: 'Mostly true', value: 'mostly_true' },
    { label: 'Sometimes true', value: 'sometimes_true' },
    { label: 'Sometimes false', value: 'sometimes_false' },
    { label: 'Uncertain', value: 'uncertain' },
    { label: 'Mostly false', value: 'mostly_false' },
    { label: 'Always false', value: 'always_false' },
  ];

  const question4Options = [
    { label: '', value: '1' },
    { label: '', value: '2' },
    { label: '', value: '3' },
    { label: '', value: '4' },
    { label: '', value: '5' },
    { label: '', value: '6' },
    { label: '', value: '7' },
  ];

  const question5Options = [
    { label: 'Ignore the request until you finish everything you are doing.', value: 'ignore' },
    { label: 'Help immediately without considering your current deadlines.', value: 'help_immediately' },
    { label: 'Acknowledge the request, assess its urgency, and communicate when or how you can assist.', value: 'acknowledge' },
    { label: 'Tell them to find someone else without discussing the situation.', value: 'redirect' },
  ];

  const skills = [
    { key: 'learningTools', label: 'Learning new tools or processes' },
    { key: 'organizingInformation', label: 'Organizing information' },
    { key: 'attentionToDetail', label: 'Paying attention to details' },
    { key: 'communicatingIdeas', label: 'Communicating ideas clearly' },
    { key: 'solvingProblems', label: 'Solving unexpected problems' },
    { key: 'managingResponsibilities', label: 'Managing multiple responsibilities' },
    { key: 'workingWithPeople', label: 'Working well with different people' },
  ];

  const verticalRankingItems = [
    { id: 'vr1', text: 'Clear communication', rank: 1 },
    { id: 'vr2', text: 'Problem solving', rank: 2 },
    { id: 'vr3', text: 'Reliability', rank: 3 },
    { id: 'vr4', text: 'Adaptability', rank: 4 },
    { id: 'vr5', text: 'Teamwork', rank: 5 },
    { id: 'vr6', text: 'Continuous learning', rank: 6 },
  ];

  const dragDropRankingItems = [
    { id: 'dr1', text: 'Integrity', color: '#DCFCE7', position: 1 },
    { id: 'dr2', text: 'Accountability', color: '#DBEAFE', position: 2 },
    { id: 'dr3', text: 'Teamwork', color: '#FEF9C3', position: 3 },
    { id: 'dr4', text: 'Continuous Learning', color: '#FCE7F3', position: 4 },
    { id: 'dr5', text: 'Adaptability', color: '#F3E8FF', position: 5 },
    { id: 'dr6', text: 'Customer Focus', color: '#FFEDD5', position: 6 },
  ];

  return (
    <div className={styles.sampleQuestionsContainer}>
      <div className={styles.headerSection}>
        <p className={styles.headerText}>
          Take a few minutes to watch the video guide and interact with the sample questions. They'll
          help you understand what to expect and how to answer each question type.{' '}
          <strong>This practice section is not part of your assessment and will not be scored.</strong>
        </p>
      </div>
      <div className={styles.divider}></div>

      {guideVideoId && <GuideVideo videoId={guideVideoId} />}

      {/* Only the header is a solid card. The expanded body stays transparent so
          each question keeps its own card on the page background, rather than
          every question sharing one large white panel. */}
      <div>
        <button
          type="button"
          className="flex w-full items-center justify-between rounded-lg border border-[#EAECF0] bg-white p-4 text-left shadow-[0px_2px_4px_0px_#0000000D] sm:px-6 sm:py-5"
          onClick={() => setIsSampleQuestionsOpen(open => !open)}
          aria-expanded={isSampleQuestionsOpen}
        >
          <h3 className="m-0 text-base font-semibold text-[#1D2939] sm:text-lg">Sample Questions</h3>
          <motion.span
            className="inline-flex shrink-0"
            animate={{ rotate: isSampleQuestionsOpen ? 180 : 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          >
            <ChevronIcon />
          </motion.span>
        </button>

        <AnimatePresence initial={false}>
          {isSampleQuestionsOpen && (
            <motion.div
              key="sample-questions"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{
                height: { duration: 0.35, ease: [0.4, 0, 0.2, 1] },
                opacity: { duration: 0.2, ease: 'easeOut' },
              }}
              style={{ overflow: 'hidden' }}
            >
              <div className="pt-5">
                <QuestionSingleRadio
                  register={register}
                  errors={errors}
                  name="question1"
                  options={question1Options}
                  questionText="You notice that a task assigned to your team is falling behind schedule because some important information has not been shared with everyone involved. What is the most effective response?"
                  questionNumber={1}
                />

                <QuestionMultiCheckbox
                  register={register}
                  watch={watch}
                  errors={errors}
                  name="question2"
                  options={question2Options}
                  questionText="You have recently started using a new system or tool that is important for your work."
                  questionNumber={2}
                  maxSelections={3}
                  labelText="Select the THREE actions most likely to help you become effective quickly."
                />

                <QuestionScale
                  register={register}
                  watch={watch}
                  errors={errors}
                  name="question3"
                  options={question3Options}
                  questionText="I usually adjust my approach when I discover a better way to complete a task. Rate how true this statement is for you."
                  questionNumber={3}
                />

                <QuestionRanking
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  errors={errors}
                  name="question4"
                  options={question4Options}
                  questionText="In the following statement, rate when you are most productive:"
                  questionNumber={4}
                  statement="I am most productive when I understand the expected outcome but have flexibility in how I complete the work."
                  labelText="Rank on a scale from most likely to least likely"
                  minLabel='Most likely'
                  maxLabel='Least likely'
                />

                <QuestionCheckboxWithRating
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  errors={errors}
                  name="question5"
                  ratingName="question5Rating"
                  options={question5Options}
                  questionText="A customer or teammate asks you for help while you are already working on an important assignment. Which response is most like what you would do?"
                  questionNumber={5}
                  labelText="Select the most likely option and rate how relevant this type of situation is to your daily work."
                />

                <QuestionSkillsRating
                  selectedSkills={selectedSkills}
                  handleRatingChange={handleRatingChange}
                  questionText="Rate how good you are at each activity."
                  questionNumber={6}
                  skills={skills}
                />
        

                <QuestionTextarea
                  register={register}
                  watch={watch}
                  errors={errors}
                  name="question7"
                  questionText="While reviewing a piece of work that is about to be submitted, you notice an error that could affect the final outcome. The deadline is approaching, and correcting the mistake may delay the submission slightly. What would you do, and why?"
                  questionNumber={7}
                  minWords={OPEN_ENDED_RESPONSE_MIN_WORDS}
                  placeholder="Enter your answer here..."
                />

                {/* The drag-ranking fields carry no card of their own (the live
                    assessment lays them out differently), so wrap them here to match
                    the other sample question cards. */}
                <div className={`${QUESTION_CARD} [&>*:last-child]:mb-0`}>
                  <VerticalRankingField
                    control={control}
                    register={register}
                    watch={watch}
                    setValue={setValue}
                    trigger={trigger}
                    errors={errors}
                    name="verticalRanking"
                    questionText="Rank the following competencies in order of importance for someone to perform well in the workplace."
                    questionNumber={8}
                    subtitle="Select a ranking for each option, from most important to least important"
                    items={verticalRankingItems}
                  />
                </div>

                <div className={`${QUESTION_CARD} [&>*:last-child]:mb-0`}>
                  <RankingField
                    control={control}
                    register={register}
                    watch={watch}
                    setValue={setValue}
                    trigger={trigger}
                    errors={errors}
                    name="dragDropRanking"
                    questionText="Drag each value into the position that best reflects your personal priorities, from highest to lowest."
                    questionNumber={9}
                    items={dragDropRankingItems}
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className={styles.divider}></div>
      <div className={styles.footerSection}>
        <p className={styles.footerText}>
          Before you start the assessment fully, I would like to collect some information from you that
          would form your Basic Expectation.
        </p>
      </div>
      <div className={styles.divider}></div>
    </div>
  );
};
